import * as React from 'react';
import { Typography, Box } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import {
  UseTreeItem2ContentSlotOwnProps as UseTreeItemContentSlotOwnProps,
  UseTreeItem2LabelSlotOwnProps as UseTreeItemLabelSlotOwnProps,
  UseTreeItem2IconContainerSlotOwnProps as UseTreeItemIconContainerSlotOwnProps
} from '@mui/x-tree-view/useTreeItem2';
import {
  TreeItem2 as TreeItem,
  TreeItem2Props as TreeItemProps,
  TreeItem2SlotProps as TreeItemSlotProps
} from '@mui/x-tree-view/TreeItem2';
import type { TreeItem2OwnerState as TreeItemOwnerState } from '@mui/x-tree-view/TreeItem2/TreeItem2.types';
import { useTreeItem2Utils as useTreeItemUtils } from '@mui/x-tree-view/hooks';
import { FolderActionsMenuButton } from './FolderActionsMenuButton';
import { useFoldersContext } from '../../contexts/useFolders.context';
import { useMemo } from 'react';
import { styles } from './FoldersListTreeItem.styles';

interface CustomContentProps extends UseTreeItemContentSlotOwnProps {
  isSelected: boolean;
  ownerState: TreeItemOwnerState;
}
const CustomContent = React.forwardRef(function CustomContent(
  { isSelected, ownerState: _, status: __, ...restProps }: CustomContentProps,
  ref: React.Ref<HTMLDivElement>
) {
  return <Box sx={{ ...styles.content, ...(isSelected && styles.contentSelected) }} {...restProps} ref={ref} />;
});
CustomContent.displayName = 'CustomContent';

export const FoldersListTreeItem = React.forwardRef(function CustomTreeItem(
  props: TreeItemProps,
  ref: React.Ref<HTMLLIElement>
) {
  const { interactions } = useTreeItemUtils({ itemId: props.itemId, children: props.children });
  const { activeFolder, rootFolder } = useFoldersContext();
  const hasChildren = useMemo(() => {
    return (
      rootFolder.subfolders &&
      rootFolder.subfolders.length > 0 &&
      rootFolder.subfolders.some((subfolder) => !!subfolder.subfolders?.length)
    );
  }, [rootFolder.subfolders]);
  const isSelected = useMemo(() => activeFolder.id === props.itemId, [activeFolder.id, props.itemId]);

  const handleIconButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    interactions.handleSelection(event);
  };

  return (
    <TreeItem
      {...props}
      ref={ref}
      slots={{
        label: CustomLabel,
        content: CustomContent,
        iconContainer: CustomIconContainer
      }}
      slotProps={
        {
          label: { isSelected },
          content: { isSelected },
          iconContainer: { onClick: handleIconButtonClick, hasChildren }
        } as TreeItemSlotProps
      }
    />
  );
});
FoldersListTreeItem.displayName = 'FoldersListTreeItem';

interface CustomIconContainerProps extends UseTreeItemIconContainerSlotOwnProps {
  hasChildren: boolean;
  ownerState: TreeItemOwnerState;
}
function CustomIconContainer({
  hasChildren,
  ownerState: _,
  ...restProps
}: CustomIconContainerProps): React.ReactElement {
  return (
    <Box
      sx={{ ...styles.icon, ...(hasChildren && styles.iconHasChildren) }}
      aria-label="expand folder"
      role="button"
      {...restProps}
    />
  );
}

interface CustomLabelProps extends UseTreeItemLabelSlotOwnProps {
  isSelected: boolean;
  ownerState: TreeItemOwnerState;
}
function CustomLabel({ children, isSelected, ownerState: _, ...props }: CustomLabelProps) {
  const {
    folderAbilities: { move: canMove, duplicate: canDuplicate, rename: canRename, delete: canDelete }
  } = useFoldersContext();
  const showActionsButton = canMove || canDuplicate || canRename || canDelete;

  return (
    <Box sx={{ ...styles.label, ...(isSelected && styles.labelSelected) }} {...props}>
      <FolderIcon sx={{ ...styles.labelIcon, ...(isSelected && styles.labelIconSelected) }} />

      <Typography
        sx={{ ...styles.labelText, ...(isSelected && styles.labelTextSelected) }}
        noWrap={true}
        component="span"
        title={children as string}
      >
        {children}
      </Typography>

      {isSelected && showActionsButton ? (
        <Box sx={styles.actionsButton}>
          <FolderActionsMenuButton />
        </Box>
      ) : null}
    </Box>
  );
}
